// Days of Cover Service - Direct Firestore Access
import { DaysOfCoverCompany, DaysOfCoverResponse, DaysOfCoverKPIs } from '../types/daysOfCover';
import { collection, getDocs, doc, getDoc, query, orderBy, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { db } from '../config/firebase';

interface DaysOfCoverSyncStatus {
  status: string; // 'completed', 'running', 'failed'
  completedAt?: any; // Firestore Timestamp
  lastSuccessful?: any; // Firestore Timestamp
  startedAt?: any; // Firestore Timestamp
  triggeredBy?: string; // 'scheduled', 'manual'
  error?: string;
}

class DaysOfCoverService {
  private companiesCollection = 'days_of_cover_companies';
  private dataCollection = 'days_of_cover';

  async getCompanies(forceReload = false): Promise<DaysOfCoverCompany[]> {
    try {
      const companiesRef = collection(db, this.companiesCollection);
      const q = query(companiesRef, orderBy('client', 'asc'));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        throw new Error('No Days of Cover companies configured in database. Please contact administrator.');
      }
      
      const companies = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DaysOfCoverCompany[];
      
      return companies;
    } catch (error) {
      console.error('Error fetching Days of Cover companies:', error);
      throw new Error(`Failed to fetch Days of Cover companies: ${error instanceof Error ? error.message : 'Unknown error'}. Please check system configuration.`);
    }
  }

  async getDaysOfCoverData(spreadsheetId: string): Promise<DaysOfCoverResponse> {
    try {
      // Step 1: Get company info to get company name
      const companyDocRef = doc(db, this.companiesCollection, spreadsheetId);
      const companyDocSnap = await getDoc(companyDocRef);
      
      if (!companyDocSnap.exists()) {
        throw new Error(`Company not found for spreadsheet ${spreadsheetId}`);
      }
      
      const companyData = companyDocSnap.data();
      const companyName = companyData.client;
      
      // Step 2: Get summary document with KPIs
      const summaryDocRef = doc(db, this.dataCollection, companyName, 'metadata', '_summary');
      const summarySnap = await getDoc(summaryDocRef);
      
      if (!summarySnap.exists()) {
        throw new Error(`No data available for company ${companyName}. Data may not be synced yet.`);
      }
      
      const summaryData = summarySnap.data();
      
      // Step 3: Get all ASIN documents
      const asinsCollectionRef = collection(db, this.dataCollection, companyName, 'asins');
      const asinsSnap = await getDocs(asinsCollectionRef);
      
      // Convert ASIN documents to rows format for backward compatibility
      const rows = asinsSnap.docs.map(doc => {
        const data = doc.data();
        return [
          data.asin || '',
          data.productName || '',
          String(data.mfnInventory || 0),
          String(data.fbaInventory || 0),
          String(data.totalInventory || 0),
          String(data.dsr || 0),
          String(data.daysOfCover || 0),
          data.active ? 'Yes' : 'No'
        ];
      });
      
      // Build KPIs object from summary data
      const kpis: DaysOfCoverKPIs = {
        averageDaysOfCover: summaryData.averageDaysOfCover || null,
        lowestDaysOfCover: summaryData.lowestDaysOfCover || null,
        highestDaysOfCover: summaryData.highestDaysOfCover || null,
        lowCoveragePercent: summaryData.lowCoveragePercent || null,
        totalMfnInventory: summaryData.totalMfnInventory || null,
        totalFbaInventory: summaryData.totalFbaInventory || null,
        totalInventory: summaryData.totalInventory || null,
        totalAsins: summaryData.totalAsins || 0,
        activeAsins: summaryData.activeAsins || 0,
        lowCoverageAsins: summaryData.lowCoverageAsins || 0,
        criticalAsins: summaryData.criticalAsins || 0,
        coverageDistribution: summaryData.coverageDistribution || {
          critical: 0,
          low: 0,
          normal: 0,
          high: 0,
          overstocked: 0
        }
      };
      
      const response: DaysOfCoverResponse = {
        spreadsheetId,
        client: companyName,
        range: 'Days of Cover!A1:H',
        headers: [
          'ASIN',
          'Product Name',
          'MFN Inventory',
          'FBA Inventory',
          'Total Inventory',
          'DSR',
          'Days of Cover',
          'Active'
        ],
        rows,
        kpis
      };
      
      return response;
    } catch (error) {
      console.error('Error fetching Days of Cover data:', error);
      throw new Error(`Failed to fetch Days of Cover data: ${error instanceof Error ? error.message : 'Unknown error'}. Please check system configuration.`);
    }
  }

  async checkHealth(): Promise<{ status: string; available: boolean }> {
    try {
      // Check if we can connect to Firestore
      const companiesRef = collection(db, this.companiesCollection);
      await getDocs(query(companiesRef));
      return { status: 'connected', available: true };
    } catch (error) {
      console.error('Firestore connection error:', error);
      return { status: 'offline', available: false };
    }
  }

  async triggerSync(): Promise<{ success: boolean; message: string }> {
    try {
      const response = await fetch('/.netlify/functions/trigger-days-of-cover-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }
      
      await response.json();
      return { success: true, message: 'Sync triggered successfully' };
    } catch (error) {
      console.error('Error triggering sync:', error);
      return { success: false, message: `Sync failed: ${error}` };
    }
  }

  async getSyncStatus(companyId: string): Promise<DaysOfCoverSyncStatus | null> {
    try {
      const statusRef = doc(db, 'days_of_cover_sync_status', companyId);
      const statusSnap = await getDoc(statusRef);
      
      if (statusSnap.exists()) {
        return statusSnap.data() as DaysOfCoverSyncStatus;
      }
      return null;
    } catch (error) {
      console.error('Error fetching sync status:', error);
      return null;
    }
  }

  subscribeSyncStatus(
    companyId: string,
    callback: (status: DaysOfCoverSyncStatus | null) => void
  ): Unsubscribe {
    const statusRef = doc(db, 'days_of_cover_sync_status', companyId);
    
    return onSnapshot(statusRef, (snapshot) => {
      if (snapshot.exists()) {
        callback(snapshot.data() as DaysOfCoverSyncStatus);
      } else {
        callback(null);
      }
    }, (error) => {
      console.error('Error in sync status subscription:', error);
      callback(null);
    });
  }
}

export const daysOfCoverService = new DaysOfCoverService();
export default daysOfCoverService;